import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ModalProcedimientoPrecio from "./ModalProcedimientoPrecio";
import SelectorEntidad from "./SelectorEntidad";
import {
  EliminarProcedimientoPrecio,
  ListarProcedimientoConPrecios,
} from "../../../Service/Service.examen";
import { ListCategoriaExamen } from "../../../Service/Service.categoriaExamen";
import { ControllerListarSegurosClinica } from "../../../Service/Services.seguros";
import { GetTokenDecoded } from "../../../utils/storeUsuario";
import * as XLSX from "xlsx";

import "../../../styles/ProcedimientosPrecios.css";

import { host } from "../../../utils/Global"; // ajusta el path si es necesario

import { guardarTarifariosMasivos } from "../../../Service/Service.examen"; // Asegúrate de crear este endpoint
import usePermissions from "../../../hook/usePermissions";
import DataTable from "../../UI/DataTable/DataTable";
import { Celda, ChipEstado, columnasVisibles } from "../../UI/DataTable/piezas";
import { CampoTexto } from "../../UI/Campo";
import Boton from "../../UI/Boton";
import MenuAcciones from "../../UI/MenuAcciones";
import Modal from "../../UI/Modal/Modal";
import { useToast } from "../../UI/Aviso";
import { MOTIVO_SIN_PERMISO } from "../../UI/BotonesExportar";
import {
  IconoMas,
  IconoEditar,
  IconoEliminar,
  IconoBuscar,
  IconoDescargar,
  IconoSubir,
  IconoCheck,
  IconoChevronAbajo,
} from "../../UI/Iconos";
import { tituloClinico } from "../../../utils/textoClinico";
import { estaSincronizado, formatearPrecio } from "../../../utils/tarifarios";

// TARIFARIOS-PERF-01: la búsqueda por código/descripción ya no se hace en el
// navegador -- la resuelve el backend (`search`) sobre el tarifario completo de
// la aseguradora, no sólo sobre la página descargada.
//
// TARIFARIOS-UI FASE 1/2: el rediseño de la pantalla. Lo que cambió y por qué,
// para que no haya que reconstruirlo leyendo el diff:
//
//   · El selector de entidad era un `<select>` de 240px en la esquina superior
//     derecha, y decide TODOS los precios de la tabla. Ahora es el control
//     dominante del bloque de contexto (ver SelectorEntidad.js).
//   · La columna "ID Contífico" enseñaba un token de 16 caracteres que nadie
//     lee. Lo que se necesita saber es SI está sincronizado; el ID completo
//     vive ahora en el modal de edición, copiable.
//   · Categoría y tipo ya venían en la respuesta del listado (`ExamenTipo`) y
//     no se pintaban. Ahora se ven, sin una sola petición nueva.
//   · Editar era la acción principal y estaba escondida en el menú de tres
//     puntos. Ahora es un botón, visible al pasar el cursor por la fila.
//   · Las descripciones se pintan en Title Case con `tituloClinico`. EL DATO
//     GUARDADO NO SE TOCA: lo que se manda a la API, lo que va a Contífico y
//     lo que sale en la factura sigue siendo el valor de la base.
//
// LO QUE NO CAMBIÓ: los endpoints, sus parámetros y el número de peticiones,
// con UNA excepción autorizada -- el filtro de categoría manda `categoria_id`,
// un parámetro que el backend ya declaraba y validaba (ver
// categoriaExamen.routes.ts). Sin él, un filtro de categoría sólo miraría las
// 20 filas de la página visible y mentiría.

// Lo que el backend NO puede hacer hoy y por tanto la pantalla NO ofrece:
//   · Contar o filtrar por "sin sincronizar". El estado se deduce de
//     `ExamenTipo.contifico_id`, que no está entre los parámetros aceptados
//     por /get_procedimientos_all_precios; filtrarlo en el navegador sólo
//     alcanzaría a la página visible y diría "3" donde hay 40. La COLUMNA sí
//     se pinta (el dato llega en cada fila); el chip contador, no.
//   · Filtrar por tipo. Mismo motivo: no hay parámetro. Categoría sí lo tiene.
const SIN_FILTRO_CATEGORIA = "";

/**
 * SEGUROS-PLANES FASE 2 · `seguroFijo` (OPCIONAL).
 *
 * Sin el prop, esta pantalla es EXACTAMENTE la de siempre: la pestaña
 * Tarifarios de /configuracion no cambia ni una linea de comportamiento.
 *
 * Con `seguroFijo = { value, label }` la aseguradora viene de la RUTA
 * (/configuracion/seguros/:id/tarifario), asi que:
 *   · no se pide la lista de aseguradoras -- una peticion menos;
 *   · no se pinta el selector de entidad ni el titulo "elige de cual": el
 *     seguro ya esta elegido y repetir la pregunta es justo lo que este
 *     rediseno viene a quitar;
 *   · todo lo demas -- buscar, filtrar por categoria, paginar, crear, editar,
 *     importar CSV, exportar -- funciona igual, contra los mismos endpoints.
 */
export default function ProcedimientoPrecio({ seguroFijo = null }) {
  // RBAC-CUTOVER-07: Crear/Cargar CSV dependen de procedimientosprecios.create,
  // Editar de procedimientosprecios.edit, Eliminar de procedimientosprecios.delete
  // (backend ya los exige en procedimientosprecios.routes.ts/categoriaExamen.routes.ts).
  const { hasPermission } = usePermissions();
  // Fail-closed, como el resto de exportaciones del sistema.
  const puedeExportar = hasPermission("procedimientosprecios.export");
  const puedeCrear = hasPermission("procedimientosprecios.create");
  const [listarProcedimeintos, setListarProcedimientos] = useState([]);
  const [showProcedimiento, setShowProcedimiento] = useState(false);
  const handleProcedimientoClose = () =>
    setShowProcedimiento(!showProcedimiento);
  const [seguros, setSeguros] = useState([]);
  const [selectedSeguroOption, setSelectedSeguroOption] = useState(null);
  // Arranca en `true`: antes la tabla pintaba "Este seguro todavía no tiene
  // procedimientos con precio" durante el primer instante, ANTES de haber
  // pedido nada -- un vacío que no era un vacío. Con la carga en servidor ese
  // parpadeo se nota más, porque la primera respuesta ya no trae 9.590 filas
  // que tarden en llegar.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generandoGeneral, setGenerandoGeneral] = useState(false);
  const [generandoTarifario, setGenerandoTarifario] = useState(false);
  const { mostrar } = useToast();

  // TARIFARIOS-PERF-01: buscar y paginar EN SERVIDOR.
  //
  // Antes esta pantalla pedía el tarifario COMPLETO de las 19 aseguradoras
  // (9.590 filas, 7,57 MB de JSON, 3,4 s de consulta medidos contra
  // producción) y filtraba por aseguradora en JavaScript para pintar como
  // máximo 1.030 filas -- y volvía a descargarlo entero en CADA cambio de
  // aseguradora. Ahora el servidor recibe `seguro_id`, `search`, `page` y
  // `limit`, y devuelve sólo la página que se ve.
  //
  // `useListaLocal` sigue siendo lo correcto para las pestañas pequeñas de
  // Configuración (Clínicas 2 filas, Doctores 52, Especialidades 225): allí
  // descargar todo y filtrar en el navegador no cuesta nada. Aquí sí costaba.
  const [busqueda, setBusqueda] = useState("");
  const [busquedaAplicada, setBusquedaAplicada] = useState("");
  const [categoria, setCategoria] = useState(SIN_FILTRO_CATEGORIA);
  const [categoriaAplicada, setCategoriaAplicada] = useState(SIN_FILTRO_CATEGORIA);
  const [categorias, setCategorias] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const [procedimientoSeleccionado, setProcedimientoSeleccionado] =
    useState(null);
  const [porBorrar, setPorBorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);

  // Carga masiva: confirmación previa y progreso. El progreso ya no se pinta
  // metiendo HTML a mano dentro de un Swal y buscando nodos por id: es estado.
  const [porCargar, setPorCargar] = useState(null); // { file, filas }
  const [progreso, setProgreso] = useState(null); // null = no hay carga en curso
  const inputArchivoRef = useRef(null);

  const fetchProcedimientos = useCallback(async (seguroId, opciones = {}) => {
    if (!seguroId) return;
    const paginaPedida = opciones.page ?? 1;
    const tamanoPedido = opciones.pageSize ?? 20;
    const textoPedido = opciones.search ?? "";
    const categoriaPedida = opciones.categoria ?? SIN_FILTRO_CATEGORIA;
    setLoading(true);
    const response = await ListarProcedimientoConPrecios({
      seguro_id: seguroId,
      page: paginaPedida,
      limit: tamanoPedido,
      ...(textoPedido ? { search: textoPedido } : {}),
      ...(categoriaPedida ? { categoria_id: Number(categoriaPedida) } : {}),
    });
    if (response?.success) {
      setListarProcedimientos(response.data || []);
      // Un seguro sin tarifario o una búsqueda sin coincidencias ya NO son un
      // error: el backend devuelve success con data vacía y la tabla pinta su
      // estado vacío. Antes el endpoint respondía success:false con la lista
      // vacía y la pantalla decía "No se pudo obtener el tarifario".
      setTotal(response.pagination?.total ?? (response.data || []).length);
      setPage(response.pagination?.page ?? paginaPedida);
      setPageSize(response.pagination?.limit ?? tamanoPedido);
      setError(null);
    } else {
      // Antes, un fallo dejaba `loading` en true para siempre.
      setListarProcedimientos([]);
      setTotal(0);
      setError(response?.message || "No se pudo obtener el tarifario.");
    }
    setBusquedaAplicada(textoPedido);
    setCategoriaAplicada(categoriaPedida);
    setLoading(false);
  }, []);

  const fetchInitialData = useCallback(async () => {
    // Con la aseguradora fijada por la ruta no hace falta el catalogo: el
    // selector no se pinta, asi que pedirlo seria descargar 23 filas para
    // tirarlas.
    if (seguroFijo) {
      setSeguros([]);
      setSelectedSeguroOption(seguroFijo);
      fetchProcedimientos(seguroFijo.value);
      return;
    }

    const dataSeguro = await ControllerListarSegurosClinica(
      GetTokenDecoded().idclinica
    );
    const segurosOptions = (dataSeguro || []).map((seg) => ({
      value: seg.idseguro,
      label: seg.nombre,
    }));
    setSeguros(segurosOptions);

    if (segurosOptions.length > 0) {
      setSelectedSeguroOption(segurosOptions[0]);
      fetchProcedimientos(segurosOptions[0].value);
    } else {
      // Sin aseguradoras no hay nada que pedir, pero la tabla arranca en
      // `loading: true` -- sin esto se quedaria cargando para siempre.
      setLoading(false);
    }
  }, [fetchProcedimientos, seguroFijo]);

  // Las categorías son un catálogo de 7 filas y ya se pedían en el modal. Se
  // piden UNA vez aquí para poder ofrecer el filtro; el modal las recibe por
  // props en vez de volver a pedirlas, así que la pantalla completa hace la
  // MISMA cantidad de peticiones que antes.
  const fetchCategorias = useCallback(async () => {
    const respuesta = await ListCategoriaExamen();
    if (respuesta?.data) {
      setCategorias(
        respuesta.data.map((c) => ({
          value: c.IdCategoriaExamen,
          label: tituloClinico(c.Descripcion),
        }))
      );
    }
  }, []);

  const handleChangeSeguro = (entidad) => {
    setSelectedSeguroOption(entidad);
    // Cambiar de aseguradora reinicia la página y la búsqueda: buscar "GLU" en
    // un tarifario y saltar a otro dejaba el filtro puesto sin decirlo.
    //
    // TRAMPA 4: la categoría SÍ se conserva -- es un filtro del catálogo
    // global, válido en cualquier entidad, y perderlo obligaría a volver a
    // ponerlo en cada salto. Y sólo se recarga la TABLA: ni la pantalla, ni el
    // menú de configuración.
    setBusqueda("");
    setPage(1);
    fetchProcedimientos(entidad.value, {
      page: 1,
      pageSize,
      search: "",
      categoria,
    });
  };

  const handleChangeCategoria = (evento) => {
    const valor = evento.target.value;
    setCategoria(valor);
    setPage(1);
    fetchProcedimientos(selectedSeguroOption?.value, {
      page: 1,
      pageSize,
      search: busquedaAplicada,
      categoria: valor,
    });
  };

  const limpiarFiltros = () => {
    setBusqueda("");
    setCategoria(SIN_FILTRO_CATEGORIA);
    setPage(1);
    fetchProcedimientos(selectedSeguroOption?.value, {
      page: 1,
      pageSize,
      search: "",
      categoria: SIN_FILTRO_CATEGORIA,
    });
  };

  const handleEdit = (record) => {
    setProcedimientoSeleccionado(record);
    setShowProcedimiento(true);
  };

  const handleSuccesSaveEdit = () => {
    fetchProcedimientos(selectedSeguroOption?.value, {
      page,
      pageSize,
      search: busquedaAplicada,
      categoria: categoriaAplicada,
    });
  };

  const handleProcedimientoNew = () => {
    setProcedimientoSeleccionado(null);
    setShowProcedimiento(true);
  };

  const handleDelete = async (data) => {
    setBorrando(true);
    const response = await EliminarProcedimientoPrecio(
      data.idprocedimiento_company_precios
    );
    setBorrando(false);
    setPorBorrar(null);
    if (response.success) {
      mostrar({ tipo: "exito", texto: response.message });
      handleSuccesSaveEdit();
    } else {
      mostrar({ tipo: "error", texto: response.message || "No se pudo eliminar el procedimiento." });
    }
  };

  /* logica cargar archivo csv o excel inical */
  // Paso 1: leer el archivo y CONTAR las filas, para poder decir cuántos
  // tarifarios se van a cargar antes de tocar nada.
  const handleFileUpload = async (e) => {
    const fileInput = e.target;
    const file = fileInput.files[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet);

      fileInput.value = "";

      if (jsonData.length === 0) {
        mostrar({ tipo: "aviso", texto: "El archivo no contiene registros." });
        return;
      }

      setPorCargar({ file, filas: jsonData.length });
    };

    reader.readAsArrayBuffer(file);
  };

  // Paso 2: subir. El polling de progreso es EXACTAMENTE el de antes -- mismo
  // endpoint, mismos 500ms -- pero el porcentaje se pinta con estado en vez de
  // escribiendo dentro de un diálogo por id.
  const confirmarCarga = async () => {
    const { file } = porCargar;
    setPorCargar(null);
    setProgreso(0);

    let progressInterval = setInterval(async () => {
      try {
        const resProgress = await fetch(
          `${host.replace(/\/$/, "")}/procedimientos_tarifario_progress`
        );
        const progressData = await resProgress.json();

        setProgreso(progressData.percentage || 0);

        // Si ya terminó, detenemos el interval
        if (!progressData.processing) {
          clearInterval(progressInterval);
        }
      } catch (err) {
        clearInterval(progressInterval);
      }
    }, 500); // cada 500ms

    // Enviamos el archivo al backend
    const res = await guardarTarifariosMasivos(file);

    // Detenemos el polling al terminar (por seguridad)
    clearInterval(progressInterval);
    setProgreso(null);

    if (res.success) {
      mostrar({ tipo: "exito", texto: res.message });
      fetchProcedimientos(selectedSeguroOption.value);

      // ✅ Descargar archivo si hay log
      if (res.file) {
        const link = document.createElement("a");
        link.href = `${host.replace(/\/$/, "")}${res.file}`;
        link.download = "log_tarifarios.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } else {
      mostrar({ tipo: "error", texto: res.message || "No se pudo guardar" });
    }
  };

  const handleDownloadExcel = async () => {
    // TARIFARIOS-PERF-01: la tabla ya sólo tiene la PÁGINA visible, así que el
    // archivo se pide aparte -- el tarifario COMPLETO de esta aseguradora (con
    // la búsqueda aplicada, si hay), sin paginar. Antes se exportaba lo que
    // hubiera en memoria; ahora se exporta lo que el usuario cree que está
    // exportando: el tarifario entero, no las 20 filas de la página.
    if (!selectedSeguroOption) {
      mostrar({ tipo: "aviso", texto: "Seleccione una aseguradora antes de exportar." });
      return;
    }
    setGenerandoTarifario(true);
    const respuesta = await ListarProcedimientoConPrecios({
      seguro_id: selectedSeguroOption.value,
      ...(busquedaAplicada ? { search: busquedaAplicada } : {}),
      ...(categoriaAplicada ? { categoria_id: Number(categoriaAplicada) } : {}),
    });
    setGenerandoTarifario(false);
    const dataSource = respuesta?.success ? respuesta.data : null;

    if (!dataSource || dataSource.length === 0) {
      mostrar({ tipo: "aviso", texto: "No hay tarifarios para exportar." });
      return;
    }

    // Estructura igual al archivo de carga masiva.
    //
    // OJO: `descripcion` va con el valor DE LA BASE, no con el Title Case que
    // se pinta en pantalla. Este archivo se vuelve a subir por la carga
    // masiva; exportar el texto embellecido reescribiría el catálogo entero
    // con la versión "bonita" en la siguiente importación.
    const dataToExport = dataSource.map((proc) => ({
      idprocedimiento: proc.idprocedimiento_company_precios || "",
      idExamen: proc.ExamenTipo?.IdExamenTipo || "",
      codigo: proc.ExamenTipo?.codigo || "",
      descripcion: proc.ExamenTipo?.descripcion || "",
      precio: proc.precio || "",
      descuento: proc.descuento || 0,
      categoria_id: proc.ExamenTipo?.categoria_id || "",
      seguro_id: proc.seguro_id || "",
      tipo: proc.ExamenTipo?.tipo || "",
      contifico_id: proc.ExamenTipo?.contifico_id || "",
    }));

    // 🧠 Crear la hoja SIN encabezado automático
    const worksheet = XLSX.utils.json_to_sheet(dataToExport, {
      skipHeader: true,
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Tarifarios");

    // 🧱 Encabezados personalizados en la fila 1
    const headers = [
      "idprocedimiento",
      "idExamen",
      "codigo",
      "descripcion",
      "precio",
      "descuento",
      "categoria_id",
      "seguro_id",
      "tipo",
      "contifico_id",
    ];

    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A1" });

    // 🔹 Definir anchos de columnas (fijo para descripcion)
    worksheet["!cols"] = [
      { wch: 14 },
      { wch: 14 },
      { wch: 10 },
      { wch: 60 }, // descripción
      { wch: 10 },
      { wch: 10 },
      { wch: 12 },
      { wch: 12 },
      { wch: 15 },
      { wch: 25 },
    ];

    // 🔹 Aplicar color, negrita y bordes finos en encabezado (solo visible si usas xlsx-style)
    const headerStyle = {
      fill: { fgColor: { rgb: "FFFF00" } }, // amarillo
      font: { bold: true },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
      alignment: { horizontal: "center", vertical: "center" },
    };

    headers.forEach((_, i) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: i });
      if (worksheet[cellAddress]) worksheet[cellAddress].s = headerStyle;
    });

    // 🔹 Generar nombre de archivo
    const nombreSeguro = selectedSeguroOption?.label || "Tarifario";
    const fileName = `${nombreSeguro.replace(/\s+/g, "_")}_export_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;

    // 🔹 Descargar
    XLSX.writeFile(workbook, fileName);

    mostrar({ tipo: "exito", texto: "El archivo se ha generado correctamente." });
  };

  // "Lista general" NO es otra vista de esta tabla: es el CATÁLOGO de
  // exámenes, sin precio y sin entidad, deduplicado por examen. Por eso sigue
  // siendo un botón de descarga junto a "Descargar tarifario" y no una
  // pestaña. Es, además, la única llamada que queda pidiendo el listado sin
  // filtrar (los ~9.600 registros); ocurre una vez, sólo al pulsarla.
  const handleDownloadExcelGeneral = async () => {
    setGenerandoGeneral(true);
    try {
      // ✅ Traemos todos los procedimientos sin filtrar por seguro
      const response = await ListarProcedimientoConPrecios();
      if (!response.success || !response.data) {
        mostrar({ tipo: "error", texto: "No se pudieron obtener los tarifarios." });
        return;
      }

      const procedimientos = response.data;

      // ✅ Agrupamos por idExamenTipo para eliminar duplicados
      const mapa = new Map();

      procedimientos.forEach((proc) => {
        const id = proc.ExamenTipo?.IdExamenTipo;
        if (!mapa.has(id)) {
          mapa.set(id, {
            idExamen: proc.ExamenTipo?.IdExamenTipo || "",
            codigo: proc.ExamenTipo?.codigo || "",
            descripcion: proc.ExamenTipo?.descripcion || "",
            categoria_id: proc.ExamenTipo?.categoria_id || "",
            tipo: proc.ExamenTipo?.tipo || "",
          });
        }
      });

      const dataToExport = Array.from(mapa.values());

      // ✅ Generamos la hoja y el libro
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Procedimientos");

      // ✅ Estilos (si usas xlsx-style)
      const headers = [
        "idExamen",
        "codigo",
        "descripcion",
        "categoria_id",
        "tipo",
      ];
      const headerStyle = {
        fill: { fgColor: { rgb: "FFFF00" } }, // amarillo
        font: { bold: true },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };

      headers.forEach((_, i) => {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: i });
        if (worksheet[cellAddress]) worksheet[cellAddress].s = headerStyle;
      });

      // ✅ Anchos de columnas
      worksheet["!cols"] = [
        { wch: 12 }, // idExamen
        { wch: 10 }, // codigo
        { wch: 60 }, // descripcion
        { wch: 12 }, // categoria_id
        { wch: 15 }, // tipo
      ];

      // ✅ Nombre del archivo
      const fileName = `Procedimientos_Todos_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      XLSX.writeFile(workbook, fileName);
      mostrar({ tipo: "exito", texto: "El archivo general se ha generado correctamente." });
    } catch (error) {
      mostrar({ tipo: "error", texto: "No se pudo generar el archivo general." });
    } finally {
      setGenerandoGeneral(false);
    }
  };
  /* logica cargar archivo csv o excel fin */

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchCategorias();
  }, [fetchCategorias]);

  // La búsqueda va al servidor, así que NO se dispara una petición por tecla:
  // se espera a que el usuario haga una pausa. Sin esto, escribir "COLESTEROL"
  // serían 11 peticiones.
  useEffect(() => {
    if (!selectedSeguroOption) return;
    if (busqueda === busquedaAplicada) return;
    const t = setTimeout(() => {
      setPage(1);
      fetchProcedimientos(selectedSeguroOption.value, {
        page: 1,
        pageSize,
        search: busqueda,
        categoria: categoriaAplicada,
      });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda, selectedSeguroOption, pageSize]);

  const hayFiltros = Boolean(busquedaAplicada) || Boolean(categoriaAplicada);

  const columnas = useMemo(
    () =>
      columnasVisibles(
        [
          {
            // El código identifica el procedimiento y la descripción lo
            // explica: se leen juntos, siempre. La descripción manda (es lo
            // que se busca con la vista) y el código va debajo, monoespaciado.
            key: "procedimiento",
            titulo: "Procedimiento",
            render: (p) => {
              const original = p.ExamenTipo?.descripcion || "";
              return (
                <div className="dt-doble">
                  {/* `title` con el valor ORIGINAL: el Title Case es para
                      leer de un vistazo, pero quien necesite el texto tal cual
                      está guardado lo tiene a un hover. */}
                  <span className="dt-doble__principal" title={original || undefined}>
                    {original ? tituloClinico(original) : <span className="dt__vacio">—</span>}
                  </span>
                  <span className="dt-doble__secundario dt-doble__secundario--mono">
                    {p.ExamenTipo?.codigo || "—"}
                  </span>
                </div>
              );
            },
          },
          {
            // Dos datos que ya llegaban en la respuesta y no se pintaban.
            key: "clasificacion",
            titulo: "Categoría / tipo",
            ancho: 190,
            render: (p) => {
              const cat = p.ExamenTipo?.categoria?.Descripcion;
              const tipo = p.ExamenTipo?.tipo;
              return (
                <div className="dt-doble">
                  <span className="dt-doble__principal tar-clasificacion__categoria" title={cat || undefined}>
                    {cat ? tituloClinico(cat) : <span className="dt__vacio">—</span>}
                  </span>
                  <span className="dt-doble__secundario" title={tipo || undefined}>
                    {tipo ? tituloClinico(tipo) : "—"}
                  </span>
                </div>
              );
            },
          },
          {
            key: "precio",
            titulo: "Precio",
            ancho: 110,
            alinear: "derecha",
            render: (p) => <Celda valor={formatearPrecio(p.precio)} mono />,
          },
          {
            // La tabla YA está filtrada por seguro con el selector de arriba: la
            // columna repetiría el mismo valor en todas las filas. Se declara
            // fijada por ese filtro y desaparece sola mientras haya uno elegido.
            key: "seguro",
            titulo: "Seguro",
            ancho: 180,
            fijadaPor: "seguro",
            render: (p) => <Celda valor={p.Seguros?.nombre} />,
          },
          {
            // ANTES: el `contifico_id` crudo, 16 caracteres que nadie lee.
            // La pregunta real es "¿está sincronizado?", y eso es un chip.
            // El ID completo no se pierde: vive en el modal de edición, con
            // botón de copiar.
            key: "contifico",
            titulo: "Contífico",
            ancho: 150,
            render: (p) => {
              const id = p.ExamenTipo?.contifico_id;
              return estaSincronizado(id) ? (
                <ChipEstado tono="exito" icono={IconoCheck} title={`ID Contífico: ${id}`}>
                  Sincronizado
                </ChipEstado>
              ) : (
                <ChipEstado tono="alerta" title="Este procedimiento no está enlazado con Contífico">
                  Sin sincronizar
                </ChipEstado>
              );
            },
          },
          {
            key: "acciones",
            titulo: "",
            ancho: 130,
            alinear: "derecha",
            render: (p) => {
              const acciones = [];
              if (hasPermission("procedimientosprecios.delete")) {
                acciones.push({
                  label: "Eliminar",
                  icono: IconoEliminar,
                  peligro: true,
                  onClick: () => setPorBorrar(p),
                });
              }
              const puedeEditar = hasPermission("procedimientosprecios.edit");
              if (!puedeEditar && acciones.length === 0) return null;
              return (
                <div className="tar-acciones">
                  {/* Editar es LA acción de esta pantalla: es un botón, no una
                      entrada del menú. Se revela al pasar el cursor (y con el
                      teclado, vía :focus-within) para no llenar la tabla de
                      azul, pero existe siempre en el DOM: nada que dependa del
                      hover puede ser la única forma de llegar a una acción. */}
                  {puedeEditar && (
                    <Boton
                      tamano="sm"
                      variante="ghost"
                      icono={IconoEditar}
                      className="tar-acciones__editar"
                      onClick={() => handleEdit(p)}
                      titulo={`Editar ${p.ExamenTipo?.codigo || "procedimiento"}`}
                    >
                      Editar
                    </Boton>
                  )}
                  {acciones.length > 0 && (
                    <MenuAcciones acciones={acciones} titulo={`Más acciones de ${p.ExamenTipo?.codigo}`} />
                  )}
                </div>
              );
            },
          },
        ],
        { seguro: selectedSeguroOption?.value }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedSeguroOption, hasPermission]
  );

  return (
    <section className="tar">
      {/* ===================== FASE 1 · bloque de contexto =====================
          Sustituye a la fila de cuatro botones y al `<select>` de la esquina.
          Lo primero que se lee es DE QUÉ TARIFARIO se trata. */}
      <div className="tar-contexto">
        {/* SEGUROS-PLANES: con la aseguradora fijada por la ruta, la cabecera y
            el selector sobran -- la ficha ya dice de quién es este tarifario, y
            volver a preguntarlo es exactamente el problema que se está
            arreglando. Las ACCIONES se quedan todas. */}
        {!seguroFijo && (
          <div className="tar-contexto__cabecera">
            <div className="tar-contexto__titulo">
              <h2>Tarifarios</h2>
              <p>
                Los precios son distintos por entidad. Elige de cuál quieres ver y editar el
                tarifario.
              </p>
            </div>
          </div>
        )}

        <div className="tar-contexto__controles">
          {!seguroFijo && (
            <>
              <SelectorEntidad
                entidades={seguros}
                seleccionada={selectedSeguroOption}
                onCambio={handleChangeSeguro}
              />

              <div className="tar-contexto__separador" />
            </>
          )}

          <div className="tar-contexto__acciones">
            {puedeCrear && (
              <Boton
                icono={IconoSubir}
                onClick={() => inputArchivoRef.current?.click()}
                data-testid="tarifario-cargar"
              >
                Importar CSV
              </Boton>
            )}
            <input
              type="file"
              id="inputTarifario"
              ref={inputArchivoRef}
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
              onChange={handleFileUpload}
            />
            {/* TARIFARIOS-EXPORT: el tarifario completo de una aseguradora es la
                lista de precios pactados de la clínica. Se descargaba con sólo
                poder VER la pantalla. Ahora exige `procedimientosprecios.export`,
                que sólo tienen los roles de administración -- y, como en el resto
                del sistema, sin el permiso el botón se ve APAGADO y dice por qué,
                no desaparece. */}
            <Boton
              icono={IconoDescargar}
              onClick={handleDownloadExcel}
              cargando={generandoTarifario}
              disabled={!puedeExportar}
              titulo={puedeExportar ? "Descargar el tarifario de esta entidad" : MOTIVO_SIN_PERMISO}
              data-testid="tarifario-descargar"
            >
              Descargar
            </Boton>
            <Boton
              icono={IconoDescargar}
              onClick={handleDownloadExcelGeneral}
              cargando={generandoGeneral}
              disabled={!puedeExportar}
              titulo={
                puedeExportar
                  ? "Descargar el catálogo de procedimientos, sin precios ni entidad"
                  : MOTIVO_SIN_PERMISO
              }
              data-testid="tarifario-descargar-general"
            >
              Lista general
            </Boton>
            {puedeCrear && (
              <Boton
                variante="primario"
                icono={IconoMas}
                onClick={handleProcedimientoNew}
                data-testid="tarifario-crear"
              >
                Nuevo procedimiento
              </Boton>
            )}
          </div>
        </div>
      </div>

      {/* ========================= FASE 2 · tabla ========================= */}
      <div className="tar-tabla">
        <div className="tar-filtros">
          <div className="tar-filtros__buscar">
            <CampoTexto
              etiqueta="Buscar procedimiento"
              etiquetaOculta
              icono={IconoBuscar}
              placeholder="Buscar por código o descripción"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              data-testid="tarifario-buscar"
            />
          </div>

          {/* El filtro de categoría lo resuelve el SERVIDOR (`categoria_id`,
              parámetro que el endpoint ya declaraba). No hay filtro de tipo
              porque el endpoint no lo acepta, y uno que sólo mirara la página
              visible mentiría. */}
          <label className="tar-filtros__select">
            <span className="tar-filtros__select-texto">
              {categoria
                ? categorias.find((c) => String(c.value) === String(categoria))?.label || "Categoría"
                : "Categoría"}
            </span>
            <IconoChevronAbajo size={15} />
            <select
              value={categoria}
              onChange={handleChangeCategoria}
              aria-label="Filtrar por categoría"
              data-testid="tarifario-categoria"
            >
              <option value={SIN_FILTRO_CATEGORIA}>Todas las categorías</option>
              {categorias.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <div className="tar-filtros__hueco" />

          <span className="tar-filtros__total" data-testid="tarifario-total">
            {loading
              ? "Cargando…"
              : `${total} ${total === 1 ? "procedimiento" : "procedimientos"}`}
          </span>
        </div>

        <DataTable
          columnas={columnas}
          filas={listarProcedimeintos}
          filaKey="idprocedimiento_company_precios"
          cargando={loading}
          error={error}
          onReintentar={() =>
            fetchProcedimientos(selectedSeguroOption?.value, {
              page,
              pageSize,
              search: busquedaAplicada,
              categoria: categoriaAplicada,
            })
          }
          etiquetaTabla="Tarifario de procedimientos"
          hayFiltrosAplicados={hayFiltros}
          onLimpiarFiltros={limpiarFiltros}
          paginacion={{
            page,
            pageSize,
            total,
            tamanos: [20, 50, 100],
            onCambio: (p, ps) => {
              setPage(p);
              setPageSize(ps);
              fetchProcedimientos(selectedSeguroOption?.value, {
                page: p,
                pageSize: ps,
                search: busquedaAplicada,
                categoria: categoriaAplicada,
              });
            },
          }}
          textoVacioSinFiltros={
            selectedSeguroOption
              ? `${selectedSeguroOption.label} todavía no tiene tarifario. Crea el primer procedimiento o impórtalo desde un archivo.`
              : "Elige una entidad para ver su tarifario."
          }
          accionesVacio={
            puedeCrear && selectedSeguroOption
              ? [
                  { label: "Nuevo procedimiento", onClick: handleProcedimientoNew, primaria: true },
                  { label: "Importar CSV", onClick: () => inputArchivoRef.current?.click() },
                ]
              : []
          }
        />
      </div>

      {/* Antes: "¿Estas seguro de eliminar el procedimiento?" -- sin decir cuál. */}
      <Modal
        abierto={Boolean(porBorrar)}
        titulo="Eliminar procedimiento"
        destructivo
        onCerrar={() => setPorBorrar(null)}
        secundario={{ label: "Cancelar" }}
        primario={{ label: "Eliminar", cargando: borrando, onClick: () => handleDelete(porBorrar) }}
      >
        <p>
          Vas a eliminar <b>{porBorrar?.ExamenTipo?.codigo}</b> —{" "}
          {tituloClinico(porBorrar?.ExamenTipo?.descripcion || "")}, del tarifario de{" "}
          <b>{selectedSeguroOption?.label}</b>. Esta acción no se puede deshacer.
        </p>
        {/* El procedimiento sigue existiendo para las demás entidades: el
            borrado es de la fila de precio, no del examen del catálogo. */}
        <p className="tar-nota">
          Se quita el precio de esta entidad. El procedimiento sigue disponible en las demás.
        </p>
      </Modal>

      <Modal
        abierto={Boolean(porCargar)}
        titulo="Cargar tarifarios"
        onCerrar={() => setPorCargar(null)}
        secundario={{ label: "Cancelar" }}
        primario={{ label: "Sí, cargar", onClick: confirmarCarga, icono: false }}
      >
        <p>
          Se van a cargar <b>{porCargar?.filas}</b> tarifarios desde{" "}
          <b>{porCargar?.file?.name}</b>. ¿Deseas proceder?
        </p>
      </Modal>

      {/* El progreso no se puede cerrar: cerrarlo no cancelaría la carga, sólo
          dejaría al usuario sin saber en qué punto está. */}
      <Modal abierto={progreso !== null} titulo="Cargando tarifarios…" ancho="aviso">
        <div className="tarifario-progreso" data-testid="tarifario-progreso">
          <div className="tarifario-progreso__texto">Progreso: {progreso}%</div>
          <div
            className="tarifario-progreso__barra"
            role="progressbar"
            aria-valuenow={progreso || 0}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Progreso de la carga de tarifarios"
          >
            <div className="tarifario-progreso__relleno" style={{ width: `${progreso || 0}%` }} />
          </div>
          <p className="tarifario-progreso__nota">
            Por favor, no cierre esta ventana mientras se cargan los tarifarios.
          </p>
        </div>
      </Modal>

      <ModalProcedimientoPrecio
        show={showProcedimiento}
        onHide={handleProcedimientoClose}
        successRefresh={handleSuccesSaveEdit}
        procedimiento={procedimientoSeleccionado}
        // FASE 3/4: el modal ya no tiene que adivinar de qué tarifario se
        // trata -- lo recibe. Es lo que evita el error más caro de la pantalla.
        entidades={seguros}
        entidadActual={selectedSeguroOption}
        categorias={categorias}
        // El borrado desde el modal se confirma DENTRO del propio modal
        // (Trampa 5: nunca un diálogo encima de otro), así que el modal
        // necesita saber si esta persona puede borrar.
        puedeEliminar={hasPermission("procedimientosprecios.delete")}
      />
    </section>
  );
}
